import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { DailyActivityPoint, ReferrerStat, DeviceStat, DashboardKPIs } from '../types';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  Calendar,
  Download,
  RefreshCw,
  Globe,
  Smartphone,
  Eye,
  Bot,
  UserCheck,
} from 'lucide-react';
import { formatNumber } from '../lib/utils';
import { useToast } from '../hooks/useToast';

export const AnalyticsPage: React.FC = () => {
  const { showToast } = useToast();
  const [days, setDays] = useState(30);
  const [dateFilterLabel, setDateFilterLabel] = useState('Last 30 Days');
  const [isLoading, setIsLoading] = useState(true);

  const [kpis, setKpis] = useState<DashboardKPIs | null>(null);
  const [clicksOverTime, setClicksOverTime] = useState<DailyActivityPoint[]>([]);
  const [referrers, setReferrers] = useState<ReferrerStat[]>([]);
  const [devices, setDevices] = useState<DeviceStat[]>([]);

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      const data = await api.get<{
        kpis: DashboardKPIs;
        clicksOverTime: DailyActivityPoint[];
        referrers: ReferrerStat[];
        devices: DeviceStat[];
      }>(`/analytics/overview?days=${days}`);

      setKpis(data.kpis);
      setClicksOverTime(data.clicksOverTime || []);
      setReferrers(data.referrers || []);
      setDevices(data.devices || []);
    } catch (err: any) {
      showToast('Analytics Load Error', err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const handleExportData = () => {
    const exportObj = {
      generatedAt: new Date().toISOString(),
      timezone: 'Asia/Kolkata',
      timeWindowDays: days,
      kpis,
      dailyClicks: clicksOverTime,
      referrers,
      devices,
    };

    const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `brototype-analytics-${days}d-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    showToast('Analytics Exported', 'Downloaded JSON summary', 'success');
  };

  const DEVICE_COLORS = ['#f97316', '#38bdf8', '#a855f7', '#64748b'];

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight">
            Marketing Performance Analytics
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            In-depth traffic classification, acquisition sources, and bot filtering (Reporting Timezone: Asia/Kolkata).
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAnalytics}
            isLoading={isLoading}
            leftIcon={<RefreshCw className="w-3.5 h-3.5" />}
          >
            Refresh
          </Button>

          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportData}
            leftIcon={<Download className="w-3.5 h-3.5" />}
          >
            Export Report
          </Button>
        </div>
      </div>

      {/* Date Filter Bar */}
      <div className="flex flex-wrap items-center gap-2 p-1.5 bg-slate-900 border border-slate-800 rounded-2xl w-fit">
        {[
          { label: 'Today', d: 1 },
          { label: 'Last 7 Days', d: 7 },
          { label: 'Last 30 Days', d: 30 },
          { label: 'Last 90 Days', d: 90 },
        ].map((btn) => (
          <button
            key={btn.d}
            onClick={() => {
              setDays(btn.d);
              setDateFilterLabel(btn.label);
            }}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
              days === btn.d
                ? 'bg-orange-600 text-white shadow-md'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* Traffic Classification Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-orange-500/20">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold uppercase text-slate-400">Total Redirect Requests</span>
              <h3 className="text-2xl font-extrabold font-mono text-white mt-1">
                {formatNumber(kpis?.totalClicks ?? 0)}
              </h3>
            </div>
            <div className="p-3 rounded-xl bg-orange-500/10 text-orange-400 border border-orange-500/20">
              <Eye className="w-5 h-5" />
            </div>
          </div>
        </Card>

        <Card className="border-emerald-500/20">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold uppercase text-slate-400">Estimated Human Visitors</span>
              <h3 className="text-2xl font-extrabold font-mono text-emerald-400 mt-1">
                {formatNumber(
                  clicksOverTime.reduce((acc, curr) => acc + curr.human_clicks, 0)
                )}
              </h3>
            </div>
            <div className="p-3 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              <UserCheck className="w-5 h-5" />
            </div>
          </div>
        </Card>

        <Card className="border-sky-500/20">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-semibold uppercase text-slate-400">Crawler Preview Requests</span>
              <h3 className="text-2xl font-extrabold font-mono text-sky-400 mt-1">
                {formatNumber(
                  clicksOverTime.reduce((acc, curr) => acc + curr.crawler_requests, 0)
                )}
              </h3>
            </div>
            <div className="p-3 rounded-xl bg-sky-500/10 text-sky-400 border border-sky-500/20">
              <Bot className="w-5 h-5" />
            </div>
          </div>
        </Card>
      </div>

      {/* Main Time Series Chart */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Daily Traffic Volume: Human vs Social Crawlers</CardTitle>
            <CardDescription>
              Shows distinct separation of genuine user clicks from preview scrapers (WhatsApp, Facebook, Twitterbot)
            </CardDescription>
          </div>
        </CardHeader>

        <div className="h-80 w-full pt-4">
          {clicksOverTime.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={clicksOverTime} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="humanArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="crawlerArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#38bdf8" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickFormatter={(val) => val.slice(5)} />
                <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0f172a',
                    borderColor: '#334155',
                    borderRadius: '12px',
                    fontSize: '12px',
                  }}
                  itemStyle={{ color: '#f1f5f9' }}
                />
                <Legend verticalAlign="top" height={36} iconType="circle" />
                <Area
                  type="monotone"
                  dataKey="human_clicks"
                  name="Human Visitors"
                  stroke="#10b981"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#humanArea)"
                />
                <Area
                  type="monotone"
                  dataKey="crawler_requests"
                  name="Crawler Preview Scrapers"
                  stroke="#38bdf8"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#crawlerArea)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-slate-500 text-xs">
              No historical data for this window.
            </div>
          )}
        </div>
      </Card>

      {/* Referrers & Device Distribution Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Referrer Channels */}
        <Card>
          <CardHeader>
            <div>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-orange-400" />
                <span>Acquisition Referrers</span>
              </CardTitle>
              <CardDescription>Top marketing and external sources</CardDescription>
            </div>
          </CardHeader>

          <div className="space-y-4 pt-2">
            {referrers.length > 0 ? (
              referrers.map((ref) => (
                <div key={ref.source} className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-slate-200">{ref.source}</span>
                    <span className="font-mono text-slate-400">
                      {formatNumber(ref.clicks)} clicks ({ref.percentage}%)
                    </span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-orange-500"
                      style={{ width: `${ref.percentage}%` }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <p className="text-xs text-slate-500 text-center py-8">
                No referrers logged for this date range.
              </p>
            )}
          </div>
        </Card>

        {/* Device Categories */}
        <Card>
          <CardHeader>
            <div>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-orange-400" />
                <span>Device Distribution</span>
              </CardTitle>
              <CardDescription>Visitor device categories</CardDescription>
            </div>
          </CardHeader>

          <div className="h-64 flex items-center justify-center pt-2">
            {devices.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={devices}
                    dataKey="count"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={4}
                  >
                    {devices.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={DEVICE_COLORS[index % DEVICE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0f172a',
                      borderColor: '#334155',
                      borderRadius: '12px',
                      fontSize: '12px',
                    }}
                  />
                  <Legend verticalAlign="bottom" height={36} iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs text-slate-500">No device data logged yet.</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};
